import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from "react";

import { getChecksForUnderstandingResource } from "@/assignments/useChecksForUnderstandingSubmissionsGet/useChecksForUnderstandingSubmissionsGet";
import fetchJson from "@/remoteState/fetchJson";
import { Assignment } from "@/types/models/Assignment.interface";
import {
  IChecksForUnderstandingContextSubmissions,
  IChecksForUnderstandingContextValue,
  IChecksForUnderstandingApiSubmissions,
  ICheckForUnderstandingQuizAnswerOption,
} from "@/types/models/CheckForUnderstandingQuiz.interface";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";

const ChecksForUnderstandingContext =
  createContext<IChecksForUnderstandingContextValue>({
    checksForUnderstandingSubmissions: {},
    addChecksForUnderstandingSubmissions: () => ({}),
  });

export function formatSubmissionsFromAPI(
  data: IChecksForUnderstandingApiSubmissions,
): IChecksForUnderstandingContextSubmissions {
  const formattedData: IChecksForUnderstandingContextSubmissions = data.reduce(
    (
      acc: { [key: string]: ICheckForUnderstandingQuizAnswerOption[] },
      submission,
    ) => {
      submission.submissions.forEach((quizSubmission) => {
        const {
          choice_id: id,
          is_correct,
          quiz_question_id: quizQuestionId,
        } = quizSubmission;
        acc[quizQuestionId] = (acc[quizQuestionId] || []).concat({
          id,
          is_correct,
        });
      });
      return acc;
    },
    {},
  );

  return formattedData;
}

export function ChecksForUnderstandingSubmissionsContextProvider({
  children,
  contentId,
  currentAssignment: assignment,
}: {
  children: ReactNode;
  contentId: string;
  currentAssignment?: Assignment | undefined;
}) {
  const { data: user } = useUser();
  const userProperties = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });

  const [
    checksForUnderstandingSubmissions,
    setChecksForUnderstandingSubmissions,
  ] = useState<IChecksForUnderstandingContextSubmissions>({});

  const addChecksForUnderstandingSubmissions = (newSubmission: {
    [questionId: string]: ICheckForUnderstandingQuizAnswerOption[];
  }) => {
    setChecksForUnderstandingSubmissions({
      ...checksForUnderstandingSubmissions,
      ...newSubmission,
    });
  };

  useEffect(() => {
    const resourceURL = getChecksForUnderstandingResource({
      contentId,
      userProperties,
      assignment,
      skip: userProperties?.isTeacher,
    });

    const skip = !resourceURL;

    if (!skip) {
      const fetchData = async () => {
        try {
          const result = (await fetchJson(
            resourceURL,
          )) as IChecksForUnderstandingApiSubmissions;

          const submissions = formatSubmissionsFromAPI(result ?? {});

          setChecksForUnderstandingSubmissions(submissions);
        } catch (e) {
          console.error(e);
        }
      };

      void fetchData();
    } // refetch data when the current assignment changes
  }, [assignment?.content_id, contentId]);

  const contextData = useMemo(
    () => ({
      checksForUnderstandingSubmissions,
      addChecksForUnderstandingSubmissions,
    }),
    // Use strip representation of the submissions to ensure that the memoized data is not stale.
    [JSON.stringify(checksForUnderstandingSubmissions)],
  );

  return (
    <ChecksForUnderstandingContext.Provider value={contextData}>
      {children}
    </ChecksForUnderstandingContext.Provider>
  );
}

export function useChecksForUnderstandingContext(): IChecksForUnderstandingContextValue {
  const checksForUnderstandingContext = useContext(
    ChecksForUnderstandingContext,
  );

  if (checksForUnderstandingContext === undefined) {
    throw new Error(
      "useChecksForUnderstandingContext must be used within a ChecksForUnderstandingSubmissionsContextProvider.",
    );
  }

  return checksForUnderstandingContext;
}

export default useChecksForUnderstandingContext;
